import { Avatar, Grid2, Typography, Divider } from '@mui/material';
import { Box } from '@mui/material';
import styles from '../../styles/styles';
import { Instance } from '../../types/liquidInstance';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useEffect } from 'react';
import { FriendlyFormatNumber, getTestnetFakeTVL } from '../../Utils';
import { useAppContext } from '../../context/context';
import { useLiquidInstanceContext } from '../../context/liquidInstanceContext';
import { MetricsData } from '../../types/yieldData';
const cardRowStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: 1,
  justifyContent: 'space-between',
  width: '100%'
} as const;

const avatarStyle = {
  width: { xs: 56, md: 72 },
  height: { xs: 56, md: 72 },
  position: 'absolute',
  top: { xs: -28, md: -36 },
  left: '50%',
  transform: 'translateX(-50%)',
  border: '3px solid white',
  zIndex: 1,
  transition: 'transform 0.2s ease-in-out',
  backgroundColor: 'rgba(26, 47, 44, 1)'
} as const;

const cardContainerStyle = {
  ...styles.boxBG,
  padding: { xs: 2, md: 3 },
  paddingTop: { xs: 5, md: 6 },
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  zIndex: 0,
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
    borderColor: 'primary.main',
    borderWidth: '1px',
    borderStyle: 'solid'
  }
} as const;

const middleCardStyle = {
  ...cardContainerStyle,
  backgroundColor: {
    xs: 'rgba(1, 171, 146, 0.12)',
    md: 'rgba(105, 172, 162, 0.49)'
  },
  '&:hover': {
    ...cardContainerStyle['&:hover'],
    backgroundColor: {
      xs: 'rgba(1, 171, 146, 0.2)',
      md: 'rgba(105, 172, 162, 0.65)'
    }
  }
} as const;

const headerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 2, md: 3 },
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: '100%',
  marginBottom: { xs: 2, md: 4 }
} as const;

function L2Card({ L2Conf, isMiddle, liveData }: { L2Conf: Instance; isMiddle?: boolean; liveData: MetricsData }) {
  const [TVL, setTVL] = useState<number>(0);
  const [APR, setAPR] = useState<number>(0);

  useEffect(() => {
    if (liveData) {
      if (liveData[L2Conf.name.toLowerCase()]) {
        const tvl = Number(liveData[L2Conf.name.toLowerCase()]?.l1Tvl);
        const apr = liveData[L2Conf.name.toLowerCase()]?.bestYield;
        setTVL(tvl);
        setAPR(apr);
      } else {
        console.log('No matching data found for', L2Conf.name);
      }
    }
  }, [L2Conf.name, liveData]);

  return (
    <Box
      sx={{
        position: 'relative',
        mt: 3,
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          '& .avatar': {
            transform: 'translate(-50%, -5px)'
          },
          '& .card': {
            transform: 'translateY(0)',
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
            borderColor: 'primary.main',
            borderWidth: '1px',
            borderStyle: 'solid'
          },
          '& .middleCard': {
            backgroundColor: {
              xs: 'rgba(1, 171, 146, 0.2)',
              md: 'rgba(105, 172, 162, 0.65)'
            }
          }
        }
      }}
    >
      <Box sx={styles.ellipseGradient} />
      <Avatar
        className="avatar"
        sx={{
          ...avatarStyle,
          transition: 'transform 0.2s ease-in-out'
        }}
        src={`/l2/${L2Conf.name.toLowerCase()}.svg`}
        alt={`${L2Conf.name} logo`}
      />
      <Box
        className={isMiddle ? 'middleCard' : 'card'}
        sx={{
          ...(isMiddle ? middleCardStyle : cardContainerStyle),
          transition: 'all 0.2s ease-in-out',
          transform: 'translateY(0)',
          '&:hover': {}
        }}
      >
        <Divider sx={styles.divider} />
        <Box sx={cardRowStyle}>
          <Typography sx={styles.greyText}>Symbol</Typography>
          <Typography>{L2Conf.L2TokenName}</Typography>
        </Box>
        <Divider sx={styles.divider} />
        <Box sx={cardRowStyle}>
          <Typography sx={styles.greyText}>TVL</Typography>
          <Typography>
            {L2Conf.live
              ? TVL !== 0
                ? '$' + FriendlyFormatNumber(TVL / 1e6)
                : 'coming soon'
              : '$' + FriendlyFormatNumber(getTestnetFakeTVL(L2Conf.L2TokenName))}
          </Typography>
        </Box>
        <Divider sx={styles.divider} />
        <Box sx={cardRowStyle}>
          <Typography sx={styles.greyText}>APY</Typography>
          <Typography>
            {L2Conf.live ? (APR !== 0 ? (APR * 10000).toFixed(2) + '%' : 'coming soon') : 'coming soon'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function GenericTable() {
  const navigate = useNavigate();
  const { instances } = useLiquidInstanceContext();
  const { instancesLiveData } = useAppContext();

  return (
    <Grid2 container spacing={2} height="100%">
      <Grid2 {...styles.sideGrids} />
      <Grid2 {...styles.centralGrid}>
        <Box sx={{ ...headerContainerStyle, gap: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
              lineHeight: 1.25,
              background: 'linear-gradient(0deg, #FFFFFF 0%, #2983E2 200%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              mt: 4
            }}
          >
            rUSDC instances
          </Typography>
          <Typography
            sx={{
              ...styles.greyText,
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}
          >
            Wrap & unwrap USDC
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ textAlign: 'center', width: '100%', marginY: { xs: 2, md: 4 } }}>
          Live
        </Typography>
        <Grid2 container spacing={{ xs: 2, md: 3 }} columns={{ xs: 12, sm: 12, md: 12 }} justifyContent="center">
          {instances.map(
            (L2Conf: Instance) =>
              L2Conf.live === true &&
              L2Conf.display === true && (
                <Grid2
                  size={{ xs: 12, sm: 6, md: 4 }}
                  key={L2Conf.name}
                  onClick={() => navigate(`/liquid-instances/${L2Conf.name.toLowerCase()}`)}
                >
                  <L2Card L2Conf={L2Conf} isMiddle={true} liveData={instancesLiveData} />
                </Grid2>
              )
          )}
        </Grid2>
        <Divider sx={{ ...styles.divider, marginY: { xs: 4, md: 4 } }} />
        <Typography variant="h6" sx={{ textAlign: 'center', width: '100%', marginY: { xs: 2, md: 4 } }}>
          Testnet
        </Typography>
        <Grid2 container spacing={{ xs: 2, md: 3 }} marginBottom={4} columns={{ xs: 12, sm: 12, md: 12 }}>
          {instances.map(
            (L2Conf: Instance) =>
              L2Conf.live === false &&
              L2Conf.display === true && (
                <Grid2
                  size={{ xs: 12, sm: 6, md: 4 }}
                  key={L2Conf.name}
                  onClick={() => navigate(`/liquid-instances/${L2Conf.name.toLowerCase()}`)}
                >
                  <L2Card L2Conf={L2Conf} isMiddle={false} liveData={instancesLiveData} />
                </Grid2>
              )
          )}
        </Grid2>
      </Grid2>
      <Grid2 {...styles.sideGrids} />
    </Grid2>
  );
}
